import React, { useEffect, useRef, useState } from "react";
import { Platform, View } from "react-native";
import {
  FlexGrid,
  FlexGridProps,
  Link,
  Message,
  useResponsive,
  theme,
} from "@clearabee/ui-library-native";
import { readAddresses } from "@clearabee/ui-sdk";
import { usePostcodeLookupField } from "components/Composite/PostcodeLookup/PostcodeLookup";
import { Input } from "components/Core/Input/Input";
import { useFormContext } from "lib/hooks/useFormContext";
import { ids, styles } from "./AddressField.styles";
import { useTranslation } from "react-i18next";
import { FieldsMap } from "./helpers";
import { FieldComponentProps } from "lib/formTypes";
import { postcode } from "lib/schemas";

interface AddressFieldProps
  extends Pick<
      FieldComponentProps,
      "labelStyles" | "errorBackground" | "errorStyles" | "invertErrorColors"
    >,
    Pick<FlexGridProps, "cellSpacing"> {
  prefix: string;
  disablePostcode?: boolean;
  initialManualMode?: boolean;
}

const fieldsMap: FieldsMap = {
  line1: "line_1",
  line2: "line_2",
  county: "county",
  city: "town_or_city",
};

export const AddressField = ({
  prefix: initialPrefix,
  disablePostcode = false,
  cellSpacing,
  initialManualMode,
  ...fieldProps
}: AddressFieldProps): React.ReactElement => {
  const [translate] = useTranslation();
  const [manualMode, setManualMode] = useState(initialManualMode);
  const { setValue, getFieldState, Field, getValues } = useFormContext();
  const { screens } = useResponsive();
  const prefix = initialPrefix ? `${initialPrefix}.` : "";
  const showDropdownError = Object.keys(fieldsMap).some(
    (key) => getFieldState(`${prefix}${key}`).error,
  );
  const manualAddress = getValues(prefix) ?? {};
  const lookupPostcode = useRef<string>(getValues(`${prefix}postcode`) || "");

  const {
    AddressDropdown,
    PostcodeInput,
    hasAddresses,
    handleSubmit,
    addressValue,
    setAddressValue,
    lat,
    lng,
  } = usePostcodeLookupField({
    onOptionSelect: (address) => {
      for (const [field, key] of Object.entries(fieldsMap)) {
        setValue(`${prefix}${field}`, address[key], { shouldValidate: true });
      }
    },
    getAddresses: readAddresses,
  });

  const handleResetAddress = () => {
    for (const field of Object.keys(fieldsMap)) {
      setValue(`${prefix}${field}`, "");
    }
    setAddressValue(undefined);
  };

  useEffect(() => {
    setValue(`${prefix}.lat`, lat);
    setValue(`${prefix}.lng`, lng);
  }, [lat, lng]);

  useEffect(() => {
    if (lookupPostcode.current) {
      handleSubmit(lookupPostcode.current, true);
    }
  }, []);

  useEffect(() => {
    if (manualMode) {
      return;
    }

    if (!disablePostcode && manualAddress.postcode !== lookupPostcode.current) {
      lookupPostcode.current = manualAddress.postcode;
      handleSubmit("", true);
      handleResetAddress();
    } else if (
      addressValue &&
      Object.entries(fieldsMap).some(
        ([key, value]) => manualAddress[key] !== addressValue[value],
      )
    ) {
      handleResetAddress();
    }
  }, [manualMode]);

  return (
    <>
      {!manualMode && (
        <>
          <FlexGrid
            cellSpacing={cellSpacing}
            rowSpacing={null}
            style={{ zIndex: 1 }}
          >
            <FlexGrid.Cell base="100%" medium="50%">
              <Field
                name={`${prefix}postcode`}
                label={translate("common.postcode")}
                styles={{ flex: 1 }}
                required
                {...fieldProps}
              >
                {disablePostcode
                  ? () => (
                      <Input.Text
                        name="postcode"
                        value={manualAddress.postcode}
                        disabled
                      />
                    )
                  : ({ field }) => (
                      <PostcodeInput
                        {...field}
                        onBlur={() => {
                          const parsedPostcode = postcode.safeParse(
                            field.value,
                          );

                          field.onBlur();

                          if (parsedPostcode.success) {
                            handleSubmit(parsedPostcode.data);
                          }
                        }}
                        onChange={(value) => {
                          field.onChange(value);
                          handleResetAddress();
                          lookupPostcode.current = manualAddress.postcode;
                        }}
                        placeholder={translate(
                          "common.form.placeholder.postcode",
                        )}
                      />
                    )}
              </Field>
            </FlexGrid.Cell>
            {(hasAddresses || Platform.OS === "web") && (
              <FlexGrid.Cell base="100%" medium="50%">
                <View style={styles.dropdown} dataSet={{ media: ids.dropdown }}>
                  <AddressDropdown
                    name={`${prefix}address`}
                    placeholder={translate("common.form.placeholder.address")}
                    styles={{
                      borderColor: showDropdownError
                        ? theme.colors.negative.base
                        : theme.colors.greyscale.lighter,
                    }}
                    defaultValue={JSON.stringify({
                      line_1: manualAddress.line1,
                      line_2: manualAddress.line2,
                      town_or_city: manualAddress.city,
                      county: manualAddress.county,
                    })}
                    {...(Platform.OS === "web" ? { isSearchable: true } : {})}
                    disabled={!hasAddresses}
                  />
                  {showDropdownError && (
                    <Message
                      type="error"
                      background
                      small
                      color={fieldProps.invertErrorColors ? "light" : undefined}
                      styles={[
                        {
                          marginTop: theme.spacing.xsmall2,
                          position: "absolute",
                          bottom: screens.medium ? -13 : -29,
                        },
                        fieldProps.invertErrorColors && {
                          backgroundColor: theme.colors.negative.base,
                        },
                      ]}
                    >
                      {translate("hwa.errors.postcodeDropdown")}
                    </Message>
                  )}
                </View>
              </FlexGrid.Cell>
            )}
            {Platform.OS !== "web" && (
              <FlexGrid.Cell base="100%">
                {showDropdownError && (
                  <Message
                    type="error"
                    background
                    small
                    color={fieldProps.invertErrorColors ? "light" : undefined}
                    styles={
                      fieldProps.invertErrorColors && {
                        backgroundColor: theme.colors.negative.base,
                      }
                    }
                  >
                    {translate("hwa.errors.postcodeDropdown")}
                  </Message>
                )}
              </FlexGrid.Cell>
            )}
          </FlexGrid>
          <View style={styles.manualLink}>
            <Link
              onPress={() => setManualMode(!manualMode)}
              color={Platform.OS === "web" ? "accent" : "brand"}
              underline
            >
              {translate("common.enterAddressManually")}
            </Link>
          </View>
        </>
      )}
      {manualMode && (
        <FlexGrid cellSpacing={cellSpacing} rowSpacing={null}>
          <FlexGrid.Cell base="100%" xsmall="50%">
            <Field
              name={`${prefix}line1`}
              label={translate("common.addressLine1")}
              required
              {...fieldProps}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate(
                    "common.form.placeholder.addressLine1",
                  )}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" xsmall="50%">
            <Field
              name={`${prefix}line2`}
              label={translate("common.addressLine2")}
              {...fieldProps}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate(
                    "common.form.placeholder.addressLine2",
                  )}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" xsmall="50%">
            <Field
              name={`${prefix}city`}
              label={translate("common.city")}
              required
              {...fieldProps}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.city")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" xsmall="50%">
            <Field
              name={`${prefix}county`}
              label={translate("common.county")}
              {...fieldProps}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.county")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" xsmall="50%">
            <Field
              name={`${prefix}postcode`}
              label={translate("common.postcode")}
              required
              {...fieldProps}
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  disabled={disablePostcode}
                  placeholder={translate("common.form.placeholder.postcode")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" xsmall="50%" style={styles.autoLink}>
            <Link
              onPress={() => setManualMode(!manualMode)}
              color={Platform.OS === "web" ? "accent" : "brand"}
              textStyles={{ textAlign: "center" }}
              underline
            >
              {translate("common.usePostcodeLookup")}
            </Link>
          </FlexGrid.Cell>
        </FlexGrid>
      )}
    </>
  );
};
